// src/services/potentialPatientsPdf.js
//
// "Export as PDF" for a study's potential-patient shortlist.
//
// Drawn with jsPDF by hand (no autotable plugin in the repo), the way
// ReceptionPage draws its consent PDF. One function, no React, no Firebase —
// it takes what the card already has on screen and lays it out.
//
// This document CONTAINS PHI (names, MRNs). It is generated in the browser and
// handed straight to the user's download folder; nothing is uploaded. The
// footer says so on every page.

import { jsPDF } from "jspdf";
import { describeRule } from "./eligibilityMatch";
import { diagnosisLabel } from "../constants/diseaseOptions";

const MARGIN = 40;
const LINE = 13;

const fmtDate = (d = new Date()) =>
  d.toLocaleString(undefined, { year: "numeric", month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" });

/**
 * @param {object} args
 * @param {object} args.study         the study document (NAME, STUDY_ID, SPONSOR, DISEASE)
 * @param {object[]} args.rules       the derived rules
 * @param {object[]} args.unmapped    criteria the AI couldn't map
 * @param {object[]} args.candidates  ranked shortlist (from rankCandidates)
 * @param {{ total:number, ruledOut:number, skipped:number }} args.stats
 * @param {string} [args.generatedBy] the signed-in user's email
 * @returns {jsPDF} the document, so the caller decides between save() and preview
 */
export const buildPotentialPatientsPdf = ({ study, rules, unmapped, candidates, stats, generatedBy }) => {
  const doc = new jsPDF({ unit: "pt", format: "letter", orientation: "landscape" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const usableW = pageW - MARGIN * 2;
  let y = MARGIN;
  let page = 1;

  const footer = () => {
    doc.setFont("helvetica", "italic").setFontSize(8).setTextColor(110);
    doc.text(
      "CONFIDENTIAL - contains protected health information. Generated by RegX for internal screening only.",
      MARGIN,
      pageH - 22
    );
    doc.text(`Page ${page}`, pageW - MARGIN, pageH - 22, { align: "right" });
    doc.setTextColor(0);
  };

  const newPage = () => {
    footer();
    doc.addPage();
    page += 1;
    y = MARGIN;
  };

  const ensure = (needed) => {
    if (y + needed > pageH - 40) newPage();
  };

  const wrapped = (text, width, fontSize = 9) => {
    doc.setFontSize(fontSize);
    return doc.splitTextToSize(String(text || ""), width);
  };

  // ---- Title block ----
  doc.setFont("helvetica", "bold").setFontSize(16);
  doc.text("Potential Patients", MARGIN, y);
  y += 20;
  doc.setFont("helvetica", "normal").setFontSize(11);
  doc.text(`${study?.NAME || "Study"}${study?.STUDY_ID ? `  (${study.STUDY_ID})` : ""}`, MARGIN, y);
  y += LINE + 2;
  doc.setFontSize(9).setTextColor(90);
  const metaBits = [
    study?.SPONSOR ? `Sponsor: ${study.SPONSOR}` : "",
    study?.DISEASE ? `Disease: ${diagnosisLabel(study.DISEASE)}` : "",
    `Generated: ${fmtDate()}`,
    generatedBy ? `By: ${generatedBy}` : "",
  ].filter(Boolean);
  doc.text(metaBits.join("    "), MARGIN, y);
  doc.setTextColor(0);
  y += LINE + 6;

  // ---- Summary ----
  doc.setFont("helvetica", "bold").setFontSize(10);
  doc.text(
    `${candidates.length} candidate${candidates.length === 1 ? "" : "s"} from ${stats.total} patient records - ${stats.ruledOut} ruled out by a criterion, ${stats.skipped} skipped (enrolled / deceased).`,
    MARGIN,
    y
  );
  y += LINE + 4;

  // ---- Rules applied ----
  doc.setFont("helvetica", "bold").setFontSize(10);
  doc.text("Criteria applied (derived from the study's inclusion / exclusion):", MARGIN, y);
  y += LINE;
  doc.setFont("helvetica", "normal").setFontSize(9);
  const colW = usableW / 2 - 10;
  const inc = rules.filter((r) => r.side === "inclusion");
  const exc = rules.filter((r) => r.side === "exclusion");
  const ruleLines = (list, title) => {
    const out = [`${title} (${list.length})`];
    list.forEach((r) => wrapped(`- ${describeRule(r)}`, colW, 9).forEach((l) => out.push(l)));
    return out;
  };
  const left = ruleLines(inc, "INCLUDE when");
  const right = ruleLines(exc, "EXCLUDE when");
  const rows = Math.max(left.length, right.length);
  for (let i = 0; i < rows; i += 1) {
    ensure(LINE);
    if (left[i]) doc.text(left[i], MARGIN, y);
    if (right[i]) doc.text(right[i], MARGIN + usableW / 2 + 10, y);
    y += LINE - 2;
  }
  y += 6;

  if (unmapped.length) {
    ensure(LINE * 2);
    doc.setFont("helvetica", "bold").setFontSize(9);
    doc.text(`Not checked by RegX - confirm by hand (${unmapped.length}):`, MARGIN, y);
    y += LINE - 1;
    doc.setFont("helvetica", "normal").setFontSize(8.5);
    unmapped.forEach((u) => {
      const lines = wrapped(`- [${u.side === "exclusion" ? "Excl" : "Incl"}] ${u.criterion}`, usableW, 8.5);
      ensure(lines.length * (LINE - 3));
      lines.forEach((l) => {
        doc.text(l, MARGIN, y);
        y += LINE - 3;
      });
    });
    y += 6;
  }

  // ---- Table ----
  const cols = [
    { key: "n", label: "#", w: 24 },
    { key: "name", label: "Patient", w: 150 },
    { key: "mrn", label: "MRN", w: 80 },
    { key: "age", label: "Age", w: 34 },
    { key: "diagnosis", label: "Diagnosis", w: 120 },
    { key: "status", label: "Status", w: 130 },
    { key: "met", label: "Met", w: 36 },
    { key: "unknown", label: "Unchecked", w: 60 },
  ];
  const fixed = cols.reduce((s, c) => s + c.w, 0);
  cols.push({ key: "notes", label: "Unchecked criteria", w: Math.max(80, usableW - fixed) });

  const header = () => {
    doc.setFillColor(235, 232, 250);
    doc.rect(MARGIN, y - 9, usableW, LINE + 2, "F");
    doc.setFont("helvetica", "bold").setFontSize(8.5);
    let x = MARGIN + 3;
    cols.forEach((c) => {
      doc.text(c.label, x, y);
      x += c.w;
    });
    y += LINE + 1;
    doc.setFont("helvetica", "normal").setFontSize(8.5);
  };

  ensure(LINE * 3);
  header();

  const ruleById = new Map(rules.map((r) => [r.id, r]));
  candidates.forEach((p, i) => {
    const unchecked = p.match.unknown
      .map((id) => ruleById.get(id))
      .filter(Boolean)
      .map((r) => describeRule(r))
      .join("; ");
    const cells = {
      n: String(i + 1),
      name: p.name || "(no name)",
      mrn: p.mrn || "-",
      age: p.age == null ? "-" : String(p.age),
      diagnosis: (p.diagnosis || []).map(diagnosisLabel).join(", ") || "-",
      status: p.status || "-",
      met: `${p.match.met.length}/${rules.length}`,
      unknown: String(p.match.unknown.length),
      notes: unchecked || "-",
    };
    const wrappedCells = cols.map((c) => wrapped(cells[c.key], c.w - 6, 8.5));
    const rowLines = Math.max(...wrappedCells.map((w) => w.length));
    const rowH = rowLines * (LINE - 3) + 4;
    if (y + rowH > pageH - 40) {
      newPage();
      header();
    }
    if (i % 2 === 1) {
      doc.setFillColor(247, 247, 250);
      doc.rect(MARGIN, y - 9, usableW, rowH, "F");
    }
    let x = MARGIN + 3;
    cols.forEach((c, ci) => {
      wrappedCells[ci].forEach((l, li) => doc.text(l, x, y + li * (LINE - 3)));
      x += c.w;
    });
    y += rowH;
  });

  if (!candidates.length) {
    doc.setFont("helvetica", "italic").setFontSize(10);
    doc.text("No patient on record matches these criteria without contradiction.", MARGIN, y + 4);
  }

  footer();
  return doc;
};

/** Build and download. Filename carries the study id and the date. */
export const exportPotentialPatientsPdf = (args) => {
  const doc = buildPotentialPatientsPdf(args);
  const stamp = new Date().toISOString().slice(0, 10);
  const idPart = String(args.study?.STUDY_ID || args.study?.NAME || "study").replace(/[^\w.-]+/g, "_");
  doc.save(`potential-patients_${idPart}_${stamp}.pdf`);
};
